import { clsx } from 'clsx'
import { useCallback, useLayoutEffect, useState } from 'react'

import { initializeGoogleIdentity, waitForGoogleIdentity } from 'components/auth/googleIdentity'
import { LoadingSpinner } from 'components/shared/LoadingSpinner'
import { showToast } from 'components/ui/toaster'
import { useAuth } from 'contexts/AuthContext'
import { webLogger } from 'utils/logger'

type GoogleAuthButtonProperties = {
  className?: string
}

const NONCE_BYTE_LENGTH = 32
const HEX_RADIX = 16
const HEX_BYTE_WIDTH = 2

const normalizeEnvironmentValue = (value: string | undefined): string | undefined => {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

const generateNonce = async (): Promise<[string, string]> => {
  const randomBytes: Uint8Array = crypto.getRandomValues(new Uint8Array(NONCE_BYTE_LENGTH))
  let nonceSource = ''
  const randomByteList = Array.from({ length: randomBytes.length }, (_, index) => randomBytes[index])
  for (const byte of randomByteList) {
    nonceSource += String.fromCodePoint(byte)
  }
  const nonce = btoa(nonceSource)
  const encoder = new TextEncoder()
  const encodedNonce = encoder.encode(nonce)
  const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce)
  const hashBytes: Uint8Array = new Uint8Array(hashBuffer)
  let hashedNonce = ''
  const hashByteList = Array.from({ length: hashBytes.length }, (_, index) => hashBytes[index])
  for (const byte of hashByteList) {
    hashedNonce += byte.toString(HEX_RADIX).padStart(HEX_BYTE_WIDTH, '0')
  }
  return [nonce, hashedNonce]
}

const useGoogleButtonIdentity = ({
  loading,
  signInWithGoogleIdToken,
  user,
}: {
  loading: boolean
  signInWithGoogleIdToken: (idToken: string, nonce?: string) => Promise<void>
  user: unknown
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [googleButtonElement, setGoogleButtonElement] = useState<HTMLDivElement | null>(null)
  const googleClientId = normalizeEnvironmentValue(process.env.REACT_APP_GOOGLE_CLIENT_ID)
  const handleGoogleButtonReference = useCallback((element: HTMLDivElement | null) => {
    setGoogleButtonElement(element)
  }, [])

  useLayoutEffect(() => {
    if (!googleButtonElement || user || loading || !googleClientId) {
      return
    }

    let isCancelled = false
    googleButtonElement.replaceChildren()

    void waitForGoogleIdentity()
      .then(async googleIdentity => {
        if (isCancelled) {
          return undefined
        }

        const [nonceValue, hashedNonce] = await generateNonce()
        if (!googleButtonElement.isConnected) {
          return undefined
        }

        initializeGoogleIdentity(googleIdentity, {
          clientId: googleClientId,
          callback: async response => {
            try {
              setSubmitting(true)
              await signInWithGoogleIdToken(response.credential, nonceValue)
            } catch (error) {
              webLogger.error('Authentication error', { error })
              showToast('Authentication failed. Please try again.', 'error')
            } finally {
              setSubmitting(false)
            }
          },
          nonce: hashedNonce,
        })

        googleButtonElement.replaceChildren()
        googleIdentity.renderButton(googleButtonElement, {
          type: 'standard',
          shape: 'pill',
          theme: 'outline',
          text: 'signin_with',
          size: 'large',
          logo_alignment: 'left',
        })

        return undefined
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return undefined
        }

        webLogger.warn('Google Sign-In script failed to initialize', {
          error: error instanceof Error ? error.message : String(error),
        })

        return undefined
      })

    return () => {
      isCancelled = true
      googleButtonElement.replaceChildren()
    }
  }, [googleButtonElement, googleClientId, loading, signInWithGoogleIdToken, user])

  return {
    handleGoogleButtonReference,
    submitting,
  }
}

export const GoogleAuthButton = ({ className = '' }: GoogleAuthButtonProperties) => {
  /* --- Section 1: Hooks --- */
  const { user, loading, signInWithGoogleIdToken } = useAuth()
  const { handleGoogleButtonReference, submitting } = useGoogleButtonIdentity({
    loading,
    signInWithGoogleIdToken,
    user,
  })

  /* --- Section 6: Render --- */
  return (
    <div aria-busy={submitting} className={clsx('auth-google-anchor', className)} ref={handleGoogleButtonReference}>
      {loading ? <LoadingSpinner /> : undefined}
    </div>
  )
}
